import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { createPaymentIntent } from "../api/payments_api";
import { isValidIsraeliID } from "../utils/validationId";

if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
  console.error("חסר מפתח Stripe. בדקי את קובץ .env");
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const getAmountInCents = (amount) => {
  const parsed = parseFloat(amount);
  if (isNaN(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * 100);
};

const CheckoutFormInner = forwardRef(
  ({ amount, currency, cardHolderName, cardHolderId, onSuccess }, ref) => {
    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const allowedCurrencies = ["ils", "usd", "eur"];
    const normalizedCurrency = currency.trim().toLowerCase();


    const processingRef = useRef(false);
    useImperativeHandle(ref, () => ({
      pay: async () => {
        if (processingRef.current) {
          throw new Error("התשלום כבר בתהליך");
          return;
        }

        processingRef.current = true;
        setErrorMessage("");

        try {
          if (!stripe || !elements) {
            throw new Error("Stripe לא מוכן");
          }

          const amountInCents = getAmountInCents(amount);
          if (amountInCents <= 0) {
            throw new Error("סכום התרומה חייב להיות גדול מאפס");
          }

          if (!currency) {
            throw new Error("חסר סוג מטבע");
          }

          if (!cardHolderName?.trim() || !cardHolderId?.trim()) {
            throw new Error("אנא מלאי שם ותעודת זהות של בעל הכרטיס");
          }



          if (!allowedCurrencies.includes(normalizedCurrency)) {
            setErrorMessage("מטבע לא נתמך");
            processingRef.current = false;
            return;
          }
          if (!isValidIsraeliID(cardHolderId)) {
            throw new Error("תעודת זהות לא תקינה");
          }

          const cardElement = elements.getElement(CardElement);
          if (!cardElement) {
            throw new Error("שדה כרטיס אשראי לא נטען");
          }


          if (!cardComplete) {
            throw new Error("אנא מלאי את מספר הכרטיס, תוקף ו-CVV");
          }

          setLoading(true);

          let clientSecret;
          try {
            const response = await createPaymentIntent(amountInCents, normalizedCurrency);
            clientSecret = response.clientSecret;

            if (!clientSecret) {
              throw new Error("חסר clientSecret מהשרת");
            }
          } catch (err) {
            console.error("PaymentIntent error:", err.response?.data || err.message);
            setErrorMessage("שגיאה ביצירת תשלום. בדקי את הקונסול");
            processingRef.current = false;
            setLoading(false);
            return;
          }


          const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: { name: cardHolderName }
            }
          });

          if (error) {
            console.error("Stripe error:", error);
            throw new Error(error.message || "שגיאה בביצוע התשלום");
          }

          if (paymentIntent?.status === "succeeded") {
            onSuccess?.(paymentIntent.id);
            return paymentIntent.id;
          }

          throw new Error("התשלום לא הושלם, נסי שוב");

        } finally {
          processingRef.current = false;
          setLoading(false);
        }
      }
    }));

    return (
      <div className="credit-form">
        <div className="form-group">
          <label>פרטי כרטיס אשראי</label>
          <CardElement
            options={{ hidePostalCode: true }}
            onChange={(e) => setCardComplete(e.complete)}
          />


        </div>

        {errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}
      </div>
    );
  }
);

const CheckoutForm = forwardRef((props, ref) => (
  <Elements stripe={stripePromise}>
    <CheckoutFormInner {...props} ref={ref} />
  </Elements>
));

export default CheckoutForm;
